// Requests from a warehouse by quantity (CR_20260915_Request_Qty §5.2): the pieces other pages use — the status chip,
// the "Request assets" form a project page opens, the project's open-requests chip and a warehouse's "To prepare" card.
// A requester picks a category and a quantity, never an asset: they never see warehouse stock (Q5).
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  isLate,
  linesText,
  type RequestOverview,
  type RequestStatus,
  sourceRequiredOnSubmit,
} from '@fleetspoke/core/requests';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useSession } from '../auth/session';
import { type SiteOverview, useCategories, useSites } from '../data/queries';
import {
  requestError,
  useRequestApprovalRequired,
  useRequests,
  useSubmitRequest,
  useProjectApprovals,
} from '../data/requests';
import { useTransferPlaces } from '../data/workflow';
import { isoDateIn } from '../lib/billing';
import { calendarDate } from '../lib/time';
import { canActAt } from '../lib/zones';
import { asPlaces } from './NewTransferDialog';

export const REQUEST_STATUS_COLOR: Record<
  RequestStatus,
  'default' | 'info' | 'warning' | 'success' | 'error'
> = {
  submitted: 'warning',
  approved: 'info',
  rejected: 'error',
  cancelled: 'default',
  expired: 'default',
  closed: 'success',
};

/** "Waiting approval", "Approved", "Closed · short"… */
export function RequestStatusChip({ r }: { r: Pick<RequestOverview, 'status' | 'closed_reason'> }) {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      color={
        r.status === 'closed' && r.closed_reason === 'short'
          ? 'warning'
          : REQUEST_STATUS_COLOR[r.status]
      }
      label={
        r.status === 'closed' && r.closed_reason
          ? t(`requests.closed_${r.closed_reason}`)
          : t(`requests.status_${r.status}`)
      }
      data-testid="request-status"
    />
  );
}

/**
 * Where a request goes, by name: the view's own names, else transfer_places() — the source warehouse's staff may not
 * see the project location itself (RLS), only its name, like the other end of a transfer (P18).
 */
export function useRequestPlace() {
  const sites = useSites();
  const places = useTransferPlaces(asPlaces(sites.data));
  return (r: Pick<RequestOverview, 'to_site_id' | 'to_site_name' | 'project_name'>) => {
    const p = places.data?.find((x) => x.site_id === r.to_site_id);
    return {
      site: r.to_site_name ?? p?.name ?? '—',
      project: r.project_name ?? p?.project_name ?? '—',
    };
  };
}

/** Today in the company timezone, and a date n days later (YYYY-MM-DD). */
export function useCompanyToday() {
  const { me } = useSession();
  return isoDateIn(me?.company?.timezone ?? 'Asia/Kuala_Lumpur');
}
const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

interface DraftLine {
  categoryId: string;
  qty: string;
  spec: string;
}

/**
 * New request (Q2–Q4): to one of the project's locations the person works at — category × quantity lines, needed by,
 * urgent, for (a subcontractor), note, and a preferred warehouse by name. A request goes straight to that warehouse
 * when nothing needs approving, and the warehouse is required then — so this has to resolve approval exactly as
 * `app.request_approval_required(company, project)` does: the project's own rule wins over the company default
 * (CR_20260919 D1). It no longer turns on `canManage`: since D7 a manager's own request waits like anyone else's.
 */
export function RequestDialog({
  projectName,
  locations,
  onClose,
}: {
  projectName: string;
  locations: SiteOverview[];
  onClose(): void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me } = useSession();
  const today = useCompanyToday();
  const categories = useCategories();
  const sites = useSites();
  const places = useTransferPlaces(asPlaces(sites.data));
  const approval = useRequestApprovalRequired(me?.company?.id);
  const projectApprovals = useProjectApprovals();
  const submit = useSubmitRequest();
  const warehouses = (places.data ?? []).filter(
    (p) => p.is_active && (p.type === 'yard' || p.type === 'warehouse'),
  );
  const [siteId, setSiteId] = useState(locations[0]?.site_id ?? '');
  const [lines, setLines] = useState<DraftLine[]>([{ categoryId: '', qty: '1', spec: '' }]);
  const [neededBy, setNeededBy] = useState(addDays(today, 1));
  const [urgent, setUrgent] = useState(false);
  const [requestedFor, setRequestedFor] = useState('');
  const [note, setNote] = useState('');
  const [preferred, setPreferred] = useState('');
  // resolve exactly as the server does, or the dialog asks for a warehouse the server does not want — or, worse,
  // lets a request through without one that the server will refuse
  const projectId = locations.find((l) => l.site_id === siteId)?.project_id ?? null;
  const projectRule = projectId ? (projectApprovals.data?.get(projectId) ?? null) : null;
  const sourceRequired = sourceRequiredOnSubmit(approval.data ?? true, projectRule);
  const setLine = (i: number, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const qtyOk = (q: string) => /^\d{1,3}$/.test(q) && Number(q) >= 1;
  const chosen = lines.map((l) => l.categoryId).filter(Boolean);
  const problem = !siteId
    ? 'requests.err_location'
    : lines.some((l) => !l.categoryId || !qtyOk(l.qty))
      ? 'requests.err_lines'
      : new Set(chosen).size !== chosen.length
        ? 'requests.err_duplicate'
        : !neededBy
          ? 'requests.err_needed_by'
          : sourceRequired && !preferred
            ? 'requests.preferred_required'
            : null;
  const send = () =>
    submit.mutate(
      {
        toSiteId: siteId,
        lines: lines.map((l) => ({ categoryId: l.categoryId, qty: Number(l.qty), spec: l.spec })),
        neededBy,
        priority: urgent ? 'urgent' : 'normal',
        requestedFor,
        note,
        preferredSourceId: preferred || null,
      },
      { onSuccess: (r) => void navigate(`/requests/${r.request_id}`) },
    );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('requests.new_title', { project: projectName })}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {locations.length > 1 ? (
            <TextField
              select
              label={t('requests.location')}
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              data-testid="request-location"
            >
              {locations.map((s) => (
                <MenuItem key={s.site_id} value={s.site_id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <Typography variant="subtitle2">{t('requests.lines_title')}</Typography>
          {lines.map((l, i) => (
            <Stack
              key={i}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'flex-start' }}
              data-testid="request-line"
            >
              <TextField
                select
                size="small"
                label={t('requests.col_category')}
                value={l.categoryId}
                onChange={(e) => setLine(i, { categoryId: e.target.value })}
                sx={{ minWidth: 180, flex: 1 }}
              >
                {(categories.data ?? []).map((c) => (
                  <MenuItem
                    key={c.id}
                    value={c.id}
                    disabled={c.id !== l.categoryId && chosen.includes(c.id)}
                  >
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="number"
                label={t('requests.qty')}
                value={l.qty}
                onChange={(e) => setLine(i, { qty: e.target.value })}
                error={!qtyOk(l.qty)}
                slotProps={{ htmlInput: { min: 1, max: 999 } }}
                sx={{ width: 100 }}
              />
              <TextField
                size="small"
                label={`${t('requests.col_spec')} (${t('common.optional')})`}
                value={l.spec}
                onChange={(e) => setLine(i, { spec: e.target.value })}
                placeholder="1.5 m × 3 m"
                sx={{ flex: 1 }}
              />
              {lines.length > 1 ? (
                <IconButton
                  aria-label={t('requests.remove_line')}
                  onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              ) : null}
            </Stack>
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setLines((ls) => [...ls, { categoryId: '', qty: '1', spec: '' }])}
              disabled={lines.length >= (categories.data?.length ?? 0)}
            >
              {t('requests.add_line')}
            </Button>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              type="date"
              label={t('requests.col_needed_by')}
              value={neededBy}
              onChange={(e) => setNeededBy(e.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: today } }}
            />
            <FormControlLabel
              control={<Switch checked={urgent} onChange={(e) => setUrgent(e.target.checked)} />}
              label={t('requests.priority_urgent')}
            />
          </Stack>
          <TextField
            label={`${t('requests.for_label')} (${t('common.optional')})`}
            value={requestedFor}
            onChange={(e) => setRequestedFor(e.target.value)}
            helperText={t('requests.for_help')}
          />
          <TextField
            select
            label={`${t('requests.preferred_label')}${sourceRequired ? '' : ` (${t('common.optional')})`}`}
            value={preferred}
            onChange={(e) => setPreferred(e.target.value)}
            helperText={
              sourceRequired ? t('requests.preferred_goes_straight') : t('requests.preferred_help')
            }
            data-testid="request-preferred"
          >
            {sourceRequired ? null : <MenuItem value="">{t('requests.preferred_none')}</MenuItem>}
            {warehouses.map((w) => (
              <MenuItem key={w.site_id} value={w.site_id}>
                {w.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={`${t('requests.note')} (${t('common.optional')})`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
          />
          {submit.error ? <Alert severity="error">{requestError(submit.error, t)}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        {problem ? (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
            {t(problem)}
          </Typography>
        ) : null}
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!!problem || submit.isPending}
          onClick={send}
          data-testid="request-submit"
        >
          {t('requests.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** A project and the locations of it the viewer may request to. */
export interface RequestableProject {
  id: string;
  name: string;
  locations: SiteOverview[];
}

/**
 * "Request assets" for one or more projects: with one it opens the form, with several it asks "Which project?"
 * first (the project user's dashboard, CR_20260924_Dashboard_Revamp §3.6). The caller decides who may request where.
 */
export function RequestAssets({
  projects,
  variant = 'outlined',
}: {
  projects: RequestableProject[];
  variant?: 'outlined' | 'contained';
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'closed' | 'pick' | RequestableProject>('closed');
  if (!projects.length) return null;
  return (
    <>
      <Button
        variant={variant}
        startIcon={variant === 'contained' ? <AddIcon /> : undefined}
        onClick={() => setStep(projects.length === 1 ? projects[0]! : 'pick')}
        data-testid="request-assets"
      >
        {t('requests.new')}
      </Button>
      {step === 'pick' ? (
        <Dialog open onClose={() => setStep('closed')} fullWidth maxWidth="xs">
          <DialogTitle>{t('dashboard.which_project')}</DialogTitle>
          <List data-testid="request-pick-project">
            {projects.map((p) => (
              <ListItemButton key={p.id} onClick={() => setStep(p)}>
                <ListItemText primary={p.name} />
              </ListItemButton>
            ))}
          </List>
          <DialogActions>
            <Button onClick={() => setStep('closed')}>{t('common.cancel')}</Button>
          </DialogActions>
        </Dialog>
      ) : null}
      {typeof step === 'object' ? (
        <RequestDialog
          projectName={step.name}
          locations={step.locations}
          onClose={() => setStep('closed')}
        />
      ) : null}
    </>
  );
}

/** "Request assets" on a project page: for whoever works at one of its active locations (Q2). */
export function RequestAssetsButton({
  projectName,
  locations,
}: {
  projectName: string;
  locations: SiteOverview[];
}) {
  const { me } = useSession();
  const mine = locations.filter((s) => s.is_active && canActAt(me, s.site_id));
  return (
    <RequestAssets
      projects={
        mine.length ? [{ id: mine[0]!.project_id ?? '', name: projectName, locations: mine }] : []
      }
    />
  );
}

function RequestRows({ rows, today }: { rows: RequestOverview[]; today: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const placeOf = useRequestPlace();
  return (
    <List dense disablePadding>
      {rows.map((r) => (
        <ListItemButton
          key={r.request_id}
          onClick={() => void navigate(`/requests/${r.request_id}`)}
          data-testid={`request-row-${r.ref}`}
        >
          <ListItemText
            primary={
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <strong>{r.ref}</strong>
                <span>{linesText(r)}</span>
                {r.priority === 'urgent' ? (
                  <Chip size="small" color="error" label={t('requests.priority_urgent')} />
                ) : null}
                {isLate(r, today) ? (
                  <Chip size="small" color="error" variant="outlined" label={t('requests.late')} />
                ) : null}
                <RequestStatusChip r={r} />
              </Stack>
            }
            secondary={`${placeOf(r).site} · ${t('requests.col_needed_by')} ${
              calendarDate(r.needed_by, i18n.language) ?? '—'
            }`}
          />
        </ListItemButton>
      ))}
    </List>
  );
}

/**
 * A project's open requests (waiting approval, or approved and not yet sent) as one chip on its Transfers tab,
 * linking to Requests filtered to the project. Nothing when there are none (owner, 2026-09-15: the section it
 * replaced was clutter).
 */
export function OpenRequestsChip({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const requests = useRequests();
  const open = (requests.data ?? []).filter(
    (r) => r.project_id === projectId && (r.status === 'submitted' || r.status === 'approved'),
  ).length;
  if (!open) return null;
  return (
    <Chip
      component={RouterLink}
      to={`/requests?project=${projectId}`}
      clickable
      variant="outlined"
      label={`${t('requests.open_chip', { count: open })} →`}
      data-testid="open-requests"
    />
  );
}

/** A warehouse's "To prepare (N)" on its Overview (§5.2): routed here, something left, urgent and late first. */
export function ToPrepareCard({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const today = useCompanyToday();
  const requests = useRequests();
  const rows = (requests.data ?? [])
    .filter((r) => r.source_site_id === siteId && r.status === 'approved' && r.qty_remaining > 0)
    .sort(
      (a, b) =>
        Number(isLate(b, today)) - Number(isLate(a, today)) ||
        Number(b.priority === 'urgent') - Number(a.priority === 'urgent') ||
        a.needed_by.localeCompare(b.needed_by),
    );
  return (
    <Card variant="outlined" sx={{ mb: 2 }} data-testid="to-prepare">
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle1">
            {t('requests.to_prepare_card', { count: rows.length })}
          </Typography>
          {rows.length ? (
            <Link
              component={RouterLink}
              to={`/requests?filter=to_prepare&warehouse=${siteId}`}
              variant="body2"
            >
              {t('requests.see_all', { count: rows.length })}
            </Link>
          ) : null}
        </Stack>
        {rows.length ? (
          <RequestRows rows={rows.slice(0, 5)} today={today} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('requests.to_prepare_none')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
